import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useSessionStore, type ActiveSession } from '../store/sessionStore';
import { useSpotStore, type FreeSpot } from '../store/spotStore';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { useScrollTracking } from '../hooks/useScrollTracking';
import { analytics } from '../hooks/useAnalytics';
import { formatLocalTime, isTodayInTz } from '../utils/time';
import { BottomNav } from '../components/ui/BottomNav';
import { AvailabilityPill } from '../components/ui/AvailabilityPill';
import { AttributeChips } from '../components/ui/AttributeChips';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { useTranslation } from '../i18n';
import {
  PARKING_STRUCTURE_TYPES,
  VEHICLE_SIZES,
  DEPENDENCY_STATUSES,
  type ParkingSpotAttributes,
  type ParkingStructureType,
  type VehicleSizeCompatibility,
  type DependencyStatus,
} from '@lotngo/shared';

// ─── types ────────────────────────────────────────────────────────────────────

interface FreeLaterSpot {
  id: string;
  spot_number: string;
  level_code: string;
  level_label: string;
  description: string | null;
  attributes?: import('@lotngo/shared').ParkingSpotAttributes;
  owner?: { id: string; apartment: string };
  availability: { source: 'schedule' | 'window'; starts_at: string; until: string };
}

interface SpotDetail {
  id: string;
  spot_number: string;
  level_label: string;
  description: string | null;
  owner: { apartment: string };
  availability: { source: 'schedule' | 'window'; until: string } | null;
}

interface MySpot {
  id: string;
  spot_number: string;
  level_label: string;
  level_code: string;
}

// ─── attribute filters ────────────────────────────────────────────────────────

interface AttrFilters {
  structure_type: ParkingStructureType[];
  vehicle_size: VehicleSizeCompatibility[];
  dependency_status: DependencyStatus[];
}

const EMPTY_ATTR_FILTERS: AttrFilters = { structure_type: [], vehicle_size: [], dependency_status: [] };

// Filtered client-side, not server-side — /spots/free is already cached
// per-building (10s TTL, generation-counter invalidation, see freeSpotsCache.ts)
// and a single building's free-spot list is small, so adding a filter
// dimension to that cache key isn't worth the complexity.
const ATTR_FILTER_GROUPS: {
  key: keyof AttrFilters;
  labelKey: string;
  icon: string;
  options: string[];
}[] = [
  { key: 'structure_type',    labelKey: 'attr_structure',  icon: 'directions_car', options: PARKING_STRUCTURE_TYPES.filter((v) => v !== 'unknown') },
  { key: 'vehicle_size',      labelKey: 'attr_vehicle_size', icon: 'straighten',    options: VEHICLE_SIZES.filter((v) => v !== 'unknown') },
  { key: 'dependency_status', labelKey: 'attr_dependency', icon: 'link',           options: DEPENDENCY_STATUSES.filter((v) => v !== 'unknown') },
];

const ATTR_VALUE_PREFIX: Record<keyof AttrFilters, string> = {
  structure_type: 'structure_',
  vehicle_size: 'vehicle_',
  dependency_status: 'dependency_',
};

function matchesAttrFilters(attributes: ParkingSpotAttributes | undefined, filters: AttrFilters): boolean {
  if (!attributes) return true; // no attribute data yet — don't hide the spot over it
  return (
    (filters.structure_type.length === 0 || filters.structure_type.includes(attributes.structure_type)) &&
    (filters.vehicle_size.length === 0 || filters.vehicle_size.includes(attributes.vehicle_size)) &&
    (filters.dependency_status.length === 0 || filters.dependency_status.includes(attributes.dependency_status))
  );
}

// Partial-claim picker model.
// Show the picker only when the host window is at least 4 hours.
// Quick-pick durations the user can choose; we filter out any that exceed
// the remaining window length so we never offer 8h on a 4h spot.
const PARTIAL_CLAIM_MIN_WINDOW_HOURS = 4;
const PARTIAL_CLAIM_OPTIONS_HOURS = [1, 2, 3, 4, 6, 8];
// Top recommendation when the window is long enough. Falls back to the
// largest option that fits if the window is smaller.
const PARTIAL_CLAIM_DEFAULT_HOURS = 8;


// ─── AvailabilityTag ──────────────────────────────────────────────────────────

function AvailabilityTag({
  source,
  until,
  timezone,
  t,
}: {
  source: 'schedule' | 'window';
  until: string;
  timezone: string;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const iso = until;
  const timeText = isTodayInTz(iso, timezone)
    ? formatLocalTime(iso, timezone, 'time')
    : formatLocalTime(iso, timezone, 'datetime');

  const isWindow = source === 'window';

  return (
    <span style={{
      display:       'inline-block',
      background:    isWindow ? '#ede7f6' : '#dde8ff',
      color:         isWindow ? '#4527a0' : '#003580',
      fontSize:      '10px',
      fontWeight:    700,
      padding:       '4px 9px',
      borderRadius:  '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace:    'nowrap',
    }}>
      {isWindow ? t('window') : t('schedule')} · {t('until')} {timeText}
    </span>
  );
}

// ─── LevelSelector ────────────────────────────────────────────────────────────

function LevelSelector({
  levels,
  value,
  onChange,
  t,
}: {
  levels: { code: string; label: string }[];
  value: string;
  onChange: (code: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = levels.find((l) => l.code === value);
  // Mirror the spot-title pattern: translate by `code`, fall back to the raw
  // `label` only when no translation exists for this level. Without this the
  // dropdown showed the English label even when the rest of the UI was in he.
  const selectedLabel =
    value === 'all'
      ? t('all_levels')
      : selected ? (t(selected.code) || selected.label) : t('all_levels');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{
        display:       'block',
        fontSize:      '10px',
        fontWeight:    700,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color:         'var(--on-surface-3)',
        marginBottom:  '8px',
      }}>
        {t('select_parking_level')}
      </label>

      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width:          '100%',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          background:     'var(--surface-low)',
          border:         'none',
          borderRadius:   '12px',
          padding:        '14px 16px',
          cursor:         'pointer',
          transition:     'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '20px' }}>
            layers
          </span>
          <span style={{ fontWeight: 600, fontSize: '15px', color: 'var(--on-surface)' }}>
            {selectedLabel}
          </span>
        </div>
        <span className="material-symbols-outlined" style={{ color: 'var(--on-surface-3)', fontSize: '20px' }}>
          expand_more
        </span>
      </button>

      {open && (
        <div style={{
          position:     'absolute',
          top:          'calc(100% + 6px)',
          left:         0,
          right:        0,
          background:   '#fff',
          borderRadius: '12px',
          boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.12)',
          overflow:     'hidden',
          zIndex:       60,
        }}>
          {[{ code: 'all', label: t('all_levels') }, ...levels].map((l, i) => (
            <button
              key={l.code}
              onClick={() => { onChange(l.code); setOpen(false); }}
              style={{
                display:    'block',
                width:      '100%',
                textAlign:  'start',
                border:     'none',
                background: l.code === value ? 'var(--primary-container)' : 'transparent',
                color:      l.code === value ? 'var(--primary)' : 'var(--on-surface)',
                fontWeight: l.code === value ? 700 : 500,
                fontSize:   '14px',
                padding:    '12px 16px',
                cursor:     'pointer',
                borderTop:  i > 0 ? '1px solid rgba(43,52,55,0.06)' : 'none',
              }}
            >
              {l.code === 'all' ? l.label : (t(l.code) || l.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FilterSheet ──────────────────────────────────────────────────────────────

function FilterSheet({
  filters,
  onChange,
  onClose,
  t,
}: {
  filters: AttrFilters;
  onChange: (filters: AttrFilters) => void;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  function toggle(key: keyof AttrFilters, value: string) {
    const current = filters[key];
    const next = current.includes(value as never)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next } as AttrFilters);
  }

  const activeCount = ATTR_FILTER_GROUPS.reduce((sum, g) => sum + filters[g.key].length, 0);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 200 }} />
      <div style={{
        position:        'fixed',
        bottom:          0,
        left:            '10px',
        right:           '10px',
        backgroundColor: '#ffffff',
        borderRadius:    '20px 20px 0 0',
        padding:         '20px 16px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex:          201,
        boxShadow:       '0 -8px 32px rgba(43,52,55,0.1)',
        maxWidth:        'calc(100% - 20px)',
        maxHeight:       '82vh',
        overflowY:       'auto',
        margin:          '0 auto',
      }}>
        <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#e5e7eb', margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--on-surface)' }}>{t('filter_spots')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--on-surface-3)' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
          </button>
        </div>

        {ATTR_FILTER_GROUPS.map((group) => (
          <div key={group.key} style={{ marginBottom: '18px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px',
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
              color: 'var(--on-surface-3)',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>{group.icon}</span>
              {t(group.labelKey)}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {group.options.map((value) => {
                const active = filters[group.key].includes(value as never);
                return (
                  <button
                    key={value}
                    onClick={() => toggle(group.key, value)}
                    style={{
                      padding:      '8px 14px',
                      borderRadius: '999px',
                      cursor:       'pointer',
                      fontFamily:   'var(--manrope)',
                      fontSize:     '12px',
                      fontWeight:   700,
                      border:       active ? '1.5px solid var(--primary)' : '1.5px solid #e2e8f0',
                      background:   active ? 'var(--primary-container)' : '#f8fafc',
                      color:        active ? 'var(--primary)' : 'var(--on-surface)',
                    }}
                  >
                    {t(`${ATTR_VALUE_PREFIX[group.key]}${value}`)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button
            onClick={() => onChange(EMPTY_ATTR_FILTERS)}
            disabled={activeCount === 0}
            style={{
              flex: 1, height: '46px', borderRadius: '10px',
              border: '1.5px solid #e3e9ec', background: '#fff',
              color: 'var(--on-surface-2)', fontSize: '14px', fontWeight: 700,
              cursor: activeCount === 0 ? 'default' : 'pointer',
              opacity: activeCount === 0 ? 0.5 : 1,
            }}
          >
            {t('clear_filters')}
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 2, height: '46px', borderRadius: '10px', border: 'none',
              background: 'var(--primary)', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t('show_results')}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── OfferSpotSheet ───────────────────────────────────────────────────────────

function OfferSpotSheet({
  spots,
  onPick,
  onClose,
  t,
}: {
  spots: MySpot[];
  onPick: (spotId: string) => void;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 200 }} />
      <div style={{
        position:        'fixed',
        bottom:          0,
        left:            '10px',
        right:           '10px',
        backgroundColor: '#ffffff',
        borderRadius:    '20px 20px 0 0',
        padding:         '20px 16px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex:          201,
        boxShadow:       '0 -8px 32px rgba(43,52,55,0.1)',
        maxWidth:        'calc(100% - 20px)',
        maxHeight:       '82vh',
        overflowY:       'auto',
        margin:          '0 auto',
      }}>
        <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#e5e7eb', margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--on-surface)' }}>{t('which_spot_to_offer')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--on-surface-3)' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {spots.map((spot) => (
            <button
              key={spot.id}
              onClick={() => onPick(spot.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
                padding: '14px 16px', borderRadius: '14px', border: '1px solid #e8edf1',
                background: '#f8fafc', cursor: 'pointer', textAlign: 'start',
              }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px', background: 'var(--primary-container)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--primary)' }}>local_parking</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '14px', color: 'var(--on-surface)' }}>
                  {t('spot_number_label')}: {spot.spot_number}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--on-surface-3)' }}>
                  {t(spot.level_code) || spot.level_label}
                </div>
              </div>
              <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--on-surface-3)' }}>chevron_right</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── SpotCard ─────────────────────────────────────────────────────────────────

function SpotCard({
  spot,
  timezone,
  disabled,
  isOwn,
  onClaim,
  onManage,
  activeAttrFilters,
  t,
}: {
  spot: FreeSpot;
  timezone: string;
  disabled: boolean;
  isOwn: boolean;
  onClaim: () => void;
  onManage: () => void;
  activeAttrFilters?: Partial<Record<keyof ParkingSpotAttributes, string[]>>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // `spot_number` is overloaded in the data model — some admins use it as a
  // short ID ("B3", "12"), others type a descriptive name. Render the same
  // parking icon either way and surface the value with a clear label above
  // or below the level title depending on length, so the level stays the
  // visual anchor of the card.
  const isLongNumber = spot.spot_number.length > 5;
  const levelTitle = t(spot.level_code) || spot.level_label;

  return (
    <div
      onClick={isOwn ? onManage : undefined}
      style={{
        background:   '#ffffff',
        borderRadius: '20px',
        padding:      '20px',
        boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
        border:       isOwn ? '1.5px solid #bbf7d0' : '1px solid transparent',
        transition:   'box-shadow 0.2s, border-color 0.2s',
        cursor:       isOwn ? 'pointer' : 'default',
      }}
    >
      {/* Top row: badge + tag */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div style={{
          width:          '48px',
          height:         '48px',
          borderRadius:   '12px',
          background:     isOwn ? '#dcfce7' : 'var(--primary-container)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
        }}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: '24px', color: isOwn ? '#16a34a' : 'var(--primary)' }}
            aria-hidden
          >
            local_parking
          </span>
        </div>

        <AvailabilityTag
          source={spot.availability.source}
          until={spot.availability.until}
          timezone={timezone}
          t={t}
        />
      </div>

      {/* Short numbers render ABOVE the level — they're the precise locator
          ("Spot 12 on Level B"). Long names render BELOW — they're descriptive
          context, secondary to the level. Both styles match the level title
          so the trio reads as a single semantic block. */}
      {!isLongNumber && (
        <div style={{
          fontFamily:   'var(--manrope)',
          fontWeight:   700,
          fontSize:     '17px',
          color:        'var(--on-surface)',
          marginBottom: '2px',
          lineHeight:   1.2,
        }}>
          {t('spot_number_label')}: {spot.spot_number}
        </div>
      )}

      <div style={{
        fontFamily:   'var(--manrope)',
        fontWeight:   700,
        fontSize:     '17px',
        color:        'var(--on-surface)',
        marginBottom: '4px',
        lineHeight:   1.2,
      }}>
        {levelTitle}
      </div>

      {isLongNumber && (
        <div style={{
          fontFamily:      'var(--manrope)',
          fontWeight:      700,
          fontSize:        '17px',
          color:           'var(--on-surface)',
          marginBottom:    '4px',
          lineHeight:      1.2,
          display:         '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow:        'hidden',
          wordBreak:       'break-word',
        }}>
          {t('spot_name_label')}: {spot.spot_number}
        </div>
      )}

      {/* Description — present-or-omit. The previous "No description"
          placeholder read as a status, not an empty state. */}
      {spot.description && (
        <div style={{
          fontSize:     '13px',
          color:        'var(--on-surface-2)',
          marginBottom: '8px',
        }}>
          {spot.description}
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <AttributeChips attributes={spot.attributes} activeFilters={activeAttrFilters} />
      </div>

      {/* Bottom row */}
      <div style={{
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        gap:            '12px',
      }}>
        {isOwn ? (
          /* Your Spot badge */
          <span style={{
            display:         'inline-flex',
            alignItems:      'center',
            gap:             '4px',
            background:      '#dcfce7',
            color:           '#15803d',
            fontSize:        '12px',
            fontWeight:      700,
            padding:         '4px 10px',
            borderRadius:    '9999px',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>home</span>
            {t('your_spot')}
          </span>
        ) : (
          /* Apartment label with info icon */
          <div style={{
            display:     'flex',
            alignItems:  'center',
            gap:         '6px',
            fontSize:    '12px',
            color:       'var(--on-surface-2)',
            fontWeight:  500,
          }}>
            <span style={{
              display:        'inline-flex',
              alignItems:     'center',
              justifyContent: 'center',
              width:          '16px',
              height:         '16px',
              borderRadius:   '50%',
              backgroundColor: 'var(--surface-low)',
              color:          'var(--primary)',
              fontSize:       '11px',
              fontWeight:     700,
            }}>
              i
            </span>
            <span>{t('apt_short')} {spot.owner?.apartment}</span>
          </div>
        )}

        {isOwn ? (
          /* Manage button */
          <button
            onClick={(e) => { e.stopPropagation(); onManage(); }}
            style={{
              height:       '36px',
              padding:      '0 20px',
              borderRadius: '8px',
              border:       '1.5px solid #16a34a',
              background:   'transparent',
              color:        '#16a34a',
              fontSize:     '13px',
              fontWeight:   600,
              cursor:       'pointer',
              whiteSpace:   'nowrap',
              transition:   'opacity 0.15s',
              flexShrink:   0,
            }}
          >
            {t('manage')}
          </button>
        ) : (
          /* Claim button */
          <button
            onClick={onClaim}
            disabled={disabled}
            style={{
              height:       '36px',
              padding:      '0 20px',
              borderRadius: '8px',
              border:       'none',
              background:   disabled ? '#e3e9ec' : 'linear-gradient(135deg, #005bc0, #004fa9)',
              color:        disabled ? '#9ca3af' : '#f7f7ff',
              fontSize:     '13px',
              fontWeight:   600,
              cursor:       disabled ? 'not-allowed' : 'pointer',
              whiteSpace:   'nowrap',
              transition:   'opacity 0.15s',
              flexShrink:   0,
            }}
          >
            {t('claim')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── PartialClaimPicker ───────────────────────────────────────────────────────
// Rendered inside ClaimModal when the host window is ≥ 4 hours. Lets the
// claimer pick a sub-duration from fixed time slots — slider positions map to
// [1h, 2h, 3h, 4h, 6h, 8h, ..., Full]. Rightmost position is always "Full".

function PartialClaimPicker({
  t,
  timezone,
  windowHours,
  effectiveStart,
  windowEnd,
  fittingDurations,
  selectedHours,
  onSelectHours,
  language,
  direction,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  timezone: string;
  windowHours: number;
  effectiveStart: Date;
  windowEnd: Date;
  fittingDurations: number[];
  selectedHours: number | null;
  onSelectHours: (h: number | null) => void;
  language: 'en' | 'he';
  direction: 'ltr' | 'rtl';
}) {
  const isRTL = direction === 'rtl';
  const fmtDuration = (hours: number) =>
    hours === 1 ? t('one_hour') : t('n_hours').replace('{n}', String(hours));

  // Build the slider stops: each fitting duration plus a trailing "full" stop.
  // `null` represents "full window" in the selection model.
  const stops: Array<number | null> = [...fittingDurations, null];
  const selectedIndex = stops.findIndex((s) => s === selectedHours);
  // Defensive: if the selection isn't in stops yet (e.g. mid-init), default to 0.
  const safeIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const selectedEnd: Date = selectedHours === null
    ? windowEnd
    : new Date(effectiveStart.getTime() + selectedHours * 60 * 60 * 1000);

  // Show day + month if the end crosses into another calendar date.
  const fmtEnd = isTodayInTz(selectedEnd.toISOString(), timezone)
    ? formatLocalTime(selectedEnd.toISOString(), timezone, 'time', language)
    : formatLocalTime(selectedEnd.toISOString(), timezone, 'datetime', language);
  const fmtStart = formatLocalTime(effectiveStart.toISOString(), timezone, 'time', language);

  const currentLabel = selectedHours === null
    ? t('full_window')
    : fmtDuration(selectedHours);

  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
        {t('claim_duration')}
      </label>
      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '14px', lineHeight: 1.5 }}>
        {t('claim_duration_help')}
      </div>

      {/* Current selection — big and clear so the user knows what they're picking */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '17px', fontWeight: 700, color: '#005bc0' }}>
          {currentLabel}
        </div>
      </div>

      {/*
        Slider — one step per fixed slot.
        In LTR: native rendering — thumb at value 0 on the LEFT, fill grows
        from left to the thumb position. The "1h" label sits on the left,
        "Full" on the right.
        In RTL: we mirror the WHOLE slider with `transform: scaleX(-1)`. This
        flips both the thumb position AND the fill bar, so:
          - Thumb at value=0 (1h) appears visually on the RIGHT ✓
          - Thumb at value=max (Full) appears visually on the LEFT ✓
          - Fill grows from the RIGHT edge toward the thumb (right-to-left),
            matching the natural RTL flow. Selecting "Full" fills the whole
            track; selecting "1h" leaves it empty.
        We do NOT reverse the value mapping — the input stays LTR semantically,
        and value 0 still means stops[0] (1h). Only the visual rendering flips.
      */}
      <input
        type="range"
        min={0}
        max={stops.length - 1}
        step={1}
        value={safeIndex}
        onChange={(e) => onSelectHours(stops[Number(e.target.value)])}
        aria-label={t('claim_duration')}
        dir="ltr"
        style={{
          width: '100%',
          accentColor: '#005bc0',
          cursor: 'pointer',
          direction: 'ltr',
          transform: isRTL ? 'scaleX(-1)' : undefined,
          margin: '4px 0 6px',
        }}
      />

      {/*
        Tick labels — positioned absolutely so each label sits directly
        under its slider stop. In RTL the position index is reversed so the
        minimum label ("1h") shows on the right and the maximum ("Full") on
        the left, matching the inverted value mapping above.
      */}
      <div
        dir="ltr"
        style={{
          position: 'relative',
          height: '14px',
          fontSize: '11px',
          marginBottom: '10px',
          direction: 'ltr',
        }}
      >
        {stops.map((stop, i) => {
          const stopIndexFromVisualStart = isRTL ? (stops.length - 1 - i) : i;
          const leftPct = stops.length === 1 ? 50 : (stopIndexFromVisualStart / (stops.length - 1)) * 100;
          return (
            <span
              key={i}
              style={{
                position: 'absolute',
                left: `${leftPct}%`,
                transform: 'translateX(-50%)',
                fontWeight: i === safeIndex ? 700 : 400,
                color: i === safeIndex ? '#005bc0' : '#9ca3af',
                whiteSpace: 'nowrap',
              }}
            >
              {stop === null ? t('full_short') : `${stop}${t('hours_short')}`}
            </span>
          );
        })}
      </div>

      {/*
        From / Until — same row, opposite ends, anchored to the same baseline.
        We set an explicit `dir` so the visual order is guaranteed regardless
        of whatever the parent inherits:
          LTR: From on the left, Until on the right.
          RTL: From on the right (mirrors the slider's min position),
               Until on the left (mirrors the slider's max position).
      */}
      <div
        dir={direction}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: '12px',
          color: '#6b7280',
        }}
      >
        <span>
          {t('from')} <strong style={{ color: '#374151' }}>{fmtStart}</strong>
        </span>
        <span>
          {t('until')} <strong style={{ color: '#374151' }}>{fmtEnd}</strong>
        </span>
      </div>
    </div>
  );
}

// ─── ClaimModal ───────────────────────────────────────────────────────────────

function ClaimModal({
  spot,
  onClose,
  onClaimed,
  timezone,
  t,
  language,
  direction,
}: {
  spot: FreeSpot | FreeLaterSpot;
  onClose: () => void;
  onClaimed: (session: ActiveSession, isFutureSession: boolean) => void;
  timezone: string;
  t: ReturnType<typeof useTranslation>['t'];
  language: 'en' | 'he';
  direction: 'ltr' | 'rtl';
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [alreadyTaken, setAlreadyTaken] = useState(false);

  const detailQ = useQuery({
    queryKey: ['spot', spot.id],
    queryFn: () => apiClient.get<{ data: SpotDetail }>(`/spots/${spot.id}`).then((r) => r.data.data),
  });

  const [selectedReason, setSelectedReason] = useState<string>('');
  // `null` = "full window" (legacy behavior). Number = hours for a partial claim.
  const [selectedHours, setSelectedHours] = useState<number | null>(null);

  // Determine if this is a future spot claim. The `availability` field exists
  // on both free-now and free-later spots, so we discriminate on the presence
  // of `starts_at` — only free_later_today rows set it (see API spotService).
  const startsAt = spot.availability?.starts_at;
  const isFutureClaim = !!startsAt;

  // Compute the host window relative to the claimer's start time.
  // - For a "claim now" → effectiveStart = now, window ends at availability.until
  // - For a future claim → effectiveStart = starts_at, window ends at availability.until
  const effectiveStart = startsAt ? new Date(startsAt) : new Date();
  const windowEnd = spot.availability?.until ? new Date(spot.availability.until) : null;
  const windowHours = windowEnd
    ? Math.max(0, (windowEnd.getTime() - effectiveStart.getTime()) / (60 * 60 * 1000))
    : 0;

  // The picker should appear only when the host window is at least 4 hours.
  // We also clamp the offered durations to ones that actually fit.
  const showPartialPicker = windowHours >= PARTIAL_CLAIM_MIN_WINDOW_HOURS;
  // Filter to only durations strictly shorter than the window — equal-to-full
  // is already covered by the "full window" option.
  const fittingDurations = PARTIAL_CLAIM_OPTIONS_HOURS.filter((h) => h < windowHours);
  // Sensible default: 8h if it fits, else the largest fitting option, else full.
  const defaultHours =
    fittingDurations.includes(PARTIAL_CLAIM_DEFAULT_HOURS)
      ? PARTIAL_CLAIM_DEFAULT_HOURS
      : fittingDurations.length > 0
        ? fittingDurations[fittingDurations.length - 1]
        : null;

  // Initialize the picker selection once we know the window size.
  useEffect(() => {
    if (showPartialPicker && selectedHours === null && defaultHours !== null) {
      setSelectedHours(defaultHours);
    }
  }, [showPartialPicker, defaultHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute the ISO `ends_at` to send to the API. `null` means "full window"
  // and we omit ends_at to keep backward-compatible behavior.
  const endsAtIso: string | null = selectedHours === null
    ? null
    : new Date(effectiveStart.getTime() + selectedHours * 60 * 60 * 1000).toISOString();

  const claimMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post<{ data: ActiveSession }>('/sessions', {
          spot_id: spot.id,
          reason: selectedReason || undefined,
          starts_at: startsAt,
          ends_at: showPartialPicker ? endsAtIso ?? undefined : undefined,
        })
        .then((r) => r.data.data),
    onSuccess: (session) => {
      analytics.trackSpotClaimed(spot.id, spot.level_code);
      if (session.status === 'scheduled') {
        analytics.trackFutureSpotClaimed(spot.id, spot.level_code);
      }
      analytics.trackSessionStarted(session.id);
      // Invalidate queries to ensure fresh data
      void qc.invalidateQueries({ queryKey: ['session', 'active'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
      onClaimed(session, session.status === 'scheduled');
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'SPOT_NOT_FREE') {
        setAlreadyTaken(true);
      } else if (code === 'CONCURRENT_SESSIONS_LIMIT_EXCEEDED') {
        setToast(t('concurrent_sessions_limit_exceeded'));
      } else if (code === 'CLAIM_OVERLAPS_EXISTING') {
        // Another claimer grabbed the chosen sub-range between picker render
        // and submit. The user can shorten or pick a different duration.
        setToast(t('claim_overlaps_existing'));
      } else if (code === 'CLAIM_DURATION_TOO_SHORT') {
        setToast(t('claim_duration_too_short'));
      } else if (code === 'CLAIM_DURATION_INVALID') {
        setToast(t('claim_duration_invalid'));
      } else if (code === 'CLAIM_EXCEEDS_WINDOW') {
        setToast(t('claim_exceeds_window'));
      } else {
        setToast(t('error_try_again'));
      }
      setTimeout(() => setToast(null), 3500);
      analytics.trackError('SPOT_CLAIM_FAILED', code);
    },
  });

  const detail = detailQ.data;

  if (alreadyTaken) {
    return (
      <>
        <div
          onClick={() => { setAlreadyTaken(false); navigate('/find-spot'); }}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 200 }}
        />
        <div style={{
          position:        'fixed',
          top:             '50%',
          left:            '50%',
          transform:       'translate(-50%, -50%)',
          width:           'calc(100% - 48px)',
          maxWidth:        '340px',
          backgroundColor: '#ffffff',
          borderRadius:    '20px',
          padding:         '24px 20px',
          zIndex:          201,
          boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.12)',
        }}>
          <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: '#dc2626' }}>
            {t('spot_already_taken')}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: 1.5 }}>
            {t('spot_taken_message')}
          </div>
          <button
            onClick={() => { setAlreadyTaken(false); navigate('/find-spot'); }}
            style={{ ...modalBtn, width: '100%' }}
          >
            {t('back_to_spots')}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 200 }}
      />
      <div style={{
        position:        'fixed',
        bottom:          0,
        left:            '10px',
        right:           '10px',
        backgroundColor: '#ffffff',
        borderRadius:    '20px 20px 0 0',
        padding:         '20px 16px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex:          201,
        boxShadow:       '0 -8px 32px rgba(43,52,55,0.1)',
        maxWidth:        'calc(100% - 20px)',
      }}>
        <div style={{
          width: '40px', height: '4px', borderRadius: '2px',
          backgroundColor: '#e5e7eb', margin: '0 auto 16px',
        }} />

        <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
          {t('claim_this_spot')}
        </div>

        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
          {t(spot.level_code) || spot.level_label} · {spot.spot_number}
        </div>

        {spot.description && (
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
            {spot.description}
          </div>
        )}

        <div style={{ marginBottom: '4px' }}>
          <AvailabilityPill source={spot.availability.source} until={spot.availability.until} timezone={timezone} />
        </div>

        {detail && (
          <div style={{ fontSize: '13px', color: '#586064', marginBottom: '16px' }}>
            {t('owner_apt')} {detail.owner.apartment}
          </div>
        )}

        {toast && (
          <div style={{
            padding: '10px', marginBottom: '12px', borderRadius: '8px',
            backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '13px', textAlign: 'center',
          }}>
            {toast}
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
            {t('reason')}
          </label>
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '13px',
              fontFamily: 'inherit',
              color: '#374151',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('select_a_reason')}</option>
            <option value="technician">{t('reason_technician')}</option>
            <option value="moving">{t('reason_moving')}</option>
            <option value="installations">{t('reason_installations')}</option>
            <option value="guests">{t('reason_guests')}</option>
            <option value="weekend_stayover">{t('reason_weekend_stayover')}</option>
            <option value="other">{t('reason_other')}</option>
          </select>
        </div>

        {showPartialPicker && (
          <PartialClaimPicker
            t={t}
            timezone={timezone}
            windowHours={windowHours}
            effectiveStart={effectiveStart}
            windowEnd={windowEnd!}
            fittingDurations={fittingDurations}
            selectedHours={selectedHours}
            onSelectHours={setSelectedHours}
            language={language}
            direction={direction}
          />
        )}

        <button
          onClick={() => claimMutation.mutate()}
          disabled={claimMutation.isPending}
          style={modalBtn}
        >
          {claimMutation.isPending ? t('claiming') : t('yes_claim_it')}
        </button>
        <button
          onClick={onClose}
          style={{ ...modalBtn, marginTop: '8px', background: '#f1f4f6', color: '#374151' }}
        >
          {t('cancel')}
        </button>
      </div>
    </>
  );
}

// ─── FindSpotPage ─────────────────────────────────────────────────────────────

export default function FindSpotPage() {
  useScrollTracking();
  const navigate           = useNavigate();
  const { setFreeSpots }   = useSpotStore();
  const { activeSessions, setActiveSessions } = useSessionStore();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const signOut  = useSignOut();
  const { t, language, direction } = useTranslation();

  const [levelFilter,        setLevelFilter]        = useState('all');
  const [attrFilters,        setAttrFilters]        = useState<AttrFilters>(EMPTY_ATTR_FILTERS);
  const [showFilterSheet,    setShowFilterSheet]    = useState(false);
  const [claimTarget,        setClaimTarget]        = useState<FreeSpot | FreeLaterSpot | null>(null);
  const [toast,              setToast]              = useState<string | null>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showOfferSheet,     setShowOfferSheet]     = useState(false);

  const user = useAuthStore((s) => s.user);

  const { data, isLoading } = useQuery({
    queryKey:        ['spots', 'free'],
    queryFn:         () =>
      apiClient
        .get<{ data: { free_now: FreeSpot[]; free_later_today: FreeLaterSpot[] } }>('/spots/free')
        .then((r) => r.data.data),
    staleTime:       30_000,
    gcTime:          5 * 60 * 1000,
    refetchInterval: 30_000,
  });

  // Lazy — only needed to power the "offer my spot" CTA in the true-empty
  // state, so skip the request on the common (spots-available) path.
  const { data: mySpots } = useQuery({
    queryKey: ['spots', 'mine'],
    queryFn:  () => apiClient.get<{ data: MySpot[] }>('/spots/mine').then((r) => r.data.data),
    enabled:  !isLoading && (data?.free_now?.length ?? 0) === 0 && (data?.free_later_today?.length ?? 0) === 0,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data?.free_now) setFreeSpots(data.free_now);
  }, [data, setFreeSpots]);

  const freeNow   = data?.free_now         ?? [];
  const freeLater = data?.free_later_today ?? [];

  const levels = Array.from(
    new Map(
      [...freeNow, ...freeLater].map((s) => [s.level_code, s.level_label]),
    ).entries(),
  ).map(([code, label]) => ({ code, label }));

  const allFiltered = levelFilter === 'all'
    ? freeNow
    : freeNow.filter((s) => s.level_code === levelFilter);

  const attrFilteredAll = allFiltered.filter((s) => matchesAttrFilters(s.attributes, attrFilters));
  const filtered      = attrFilteredAll.filter((s) => s.owner?.id !== user?.id);
  const filteredOwn   = attrFilteredAll.filter((s) => s.owner?.id === user?.id);

  const allFilteredLater = levelFilter === 'all'
    ? freeLater
    : freeLater.filter((s) => s.level_code === levelFilter);

  const attrFilteredAllLater = allFilteredLater.filter((s) => matchesAttrFilters(s.attributes, attrFilters));
  const filteredLater    = attrFilteredAllLater.filter((s) => s.owner?.id !== user?.id);
  const filteredOwnLater = attrFilteredAllLater.filter((s) => s.owner?.id === user?.id);

  const activeAttrFilterCount = ATTR_FILTER_GROUPS.reduce((sum, g) => sum + attrFilters[g.key].length, 0);
  const hasNowSpotsBeforeAttrFilter = allFiltered.length > 0;
  // Building-wide, unfiltered by level or attributes — true "nothing to browse"
  // vs. "your filters matched nothing." Only the former hides the pickers.
  const hasAnyFreeSpotsAtAll = freeNow.length > 0 || freeLater.length > 0;

  function handleClaimed(session: ActiveSession, isFutureSession: boolean) {
    // Only add to active sessions if it's actually active now
    if (session.status === 'active') {
      setActiveSessions([...activeSessions, session]);
    }
    // For scheduled sessions, navigate but don't add to active sessions
    navigate('/');
  }

  function tryClaimSpot(spot: FreeSpot | FreeLaterSpot) {
    analytics.trackClaimInitiated(spot.id, spot.level_code);
    setClaimTarget(spot);
  }

  function handleLevelFilterChange(levelCode: string) {
    setLevelFilter(levelCode);
    analytics.trackSearchFiltered(levelCode === 'all' ? undefined : levelCode);
  }

  function handleAttrFiltersChange(next: AttrFilters) {
    setAttrFilters(next);
    const count = ATTR_FILTER_GROUPS.reduce((sum, g) => sum + next[g.key].length, 0);
    analytics.trackAttributeFilterApplied(count);
  }

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>

      {/* ── Top bar ── */}
      <header className="topbar" style={{
        position:       'sticky',
        top:            0,
        zIndex:         50,
        height:         '64px',
        padding:        '0 24px',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        boxShadow:      '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => navigate('/profile')} style={iconBtn} title={t('settings')} aria-label={t('settings')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>settings</span>
          </button>
          <button onClick={() => setShowSignOutConfirm(true)} style={iconBtn} title={t('sign_out')} aria-label={t('sign_out')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
          </button>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div style={{
          margin: '8px 16px 0', padding: '10px 14px', borderRadius: '8px',
          backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '13px',
        }}>
          {toast}
        </div>
      )}

      <div className="page-body">

        {/* ── Hero: headline + available count ── */}
        <section style={{ marginBottom: '20px' }}>
          <div style={{
            display:        'flex',
            alignItems:     'baseline',
            justifyContent: 'space-between',
            marginBottom:   '16px',
          }}>
            <h2 style={{
              fontFamily:    'var(--manrope)',
              fontSize:      '28px',
              fontWeight:    800,
              color:         'var(--on-surface)',
              letterSpacing: '-0.02em',
              margin:        0,
              lineHeight:    1.15,
            }}>
              {t('find_a_spot')}
            </h2>
            <span style={{
              fontSize:     '13px',
              fontWeight:   600,
              color:        'var(--primary)',
              background:   'var(--primary-container)',
              padding:      '5px 13px',
              borderRadius: '9999px',
              whiteSpace:   'nowrap',
              flexShrink:   0,
              marginLeft:   '12px',
            }}>
              {attrFilteredAll.length} {t('available')}
            </span>
          </div>

          {/* Level selector card + filters button — hidden when there's truly
              nothing to browse building-wide; kept when the zero-count is
              just the user's own filter/level selection matching nothing. */}
          {hasAnyFreeSpotsAtAll && (
          <>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{
              flex:         1,
              background:   '#ffffff',
              borderRadius: '20px',
              padding:      '20px',
              boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
            }}>
              <LevelSelector
                levels={levels}
                value={levelFilter}
                onChange={handleLevelFilterChange}
                t={t}
              />
            </div>
            <button
              onClick={() => setShowFilterSheet(true)}
              style={{
                position:       'relative',
                display:        'flex',
                flexDirection:  'column',
                alignItems:     'center',
                justifyContent: 'center',
                gap:            '2px',
                width:          '68px',
                flexShrink:     0,
                border:         'none',
                borderRadius:   '20px',
                background:     activeAttrFilterCount > 0 ? 'var(--primary-container)' : '#ffffff',
                boxShadow:      '0 8px 24px -4px rgba(43,52,55,0.06)',
                cursor:         'pointer',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '22px', color: activeAttrFilterCount > 0 ? 'var(--primary)' : 'var(--on-surface-2)' }}>
                tune
              </span>
              <span style={{ fontSize: '10px', fontWeight: 700, color: activeAttrFilterCount > 0 ? 'var(--primary)' : 'var(--on-surface-3)' }}>
                {t('filters')}
              </span>
              {activeAttrFilterCount > 0 && (
                <span style={{
                  position: 'absolute', top: '8px', right: '8px', minWidth: '16px', height: '16px',
                  borderRadius: '999px', background: 'var(--primary)', color: '#fff',
                  fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', padding: '0 3px',
                }}>
                  {activeAttrFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Active attribute-filter chips (removable) */}
          {activeAttrFilterCount > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
              {ATTR_FILTER_GROUPS.flatMap((group) =>
                attrFilters[group.key].map((value) => (
                  <span
                    key={`${group.key}:${value}`}
                    onClick={() => handleAttrFiltersChange({
                      ...attrFilters,
                      [group.key]: attrFilters[group.key].filter((v) => v !== value),
                    } as AttrFilters)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '5px 10px', borderRadius: '999px',
                      background: 'var(--primary-container)', color: 'var(--primary)',
                      fontFamily: 'var(--manrope)', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {t(`${ATTR_VALUE_PREFIX[group.key]}${value}`)}
                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>close</span>
                  </span>
                )),
              )}
            </div>
          )}
          </>
          )}
        </section>

        {/* ── Spot list ── */}
        {isLoading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '48px 0' }}>
            {t('loading')}
          </div>
        ) : !hasAnyFreeSpotsAtAll ? (
          <div style={{
            background:   'var(--surface-low)',
            borderRadius: '24px',
            padding:      '40px 24px',
            textAlign:    'center',
            boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.04)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
              <div style={{
                width: '88px', height: '88px', borderRadius: '9999px',
                background: 'var(--surface-high)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '44px', color: 'var(--on-surface-3)' }}>
                  search_off
                </span>
              </div>
            </div>
            <h2 style={{ fontFamily: 'var(--manrope)', fontSize: '22px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '10px' }}>
              {t('no_free_spots')}
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, marginBottom: '28px', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto' }}>
              {t('no_free_spots_empty_desc')}
            </p>
            <button
              onClick={() => navigate('/requests', { state: { openCreateForm: true } })}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                margin: '0 auto', padding: '14px 32px', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                color: '#f7f7ff', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0,91,192,0.25)',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add_circle</span>
              {t('request_a_spot')}
            </button>
            {mySpots && mySpots.length > 0 && (
              <button
                onClick={() => {
                  if (mySpots.length === 1) {
                    navigate(`/spots/${mySpots[0].id}/free`);
                  } else {
                    setShowOfferSheet(true);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  margin: '10px auto 0', padding: '14px 32px', borderRadius: '12px',
                  border: '1.5px solid var(--primary)', background: '#fff',
                  color: 'var(--primary)', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>local_parking</span>
                {t('offer_my_spot')}
              </button>
            )}
          </div>
        ) : attrFilteredAll.length === 0 ? (
          <div style={{
            background:   '#ffffff',
            borderRadius: '20px',
            padding:      '40px 20px',
            boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
            textAlign:    'center',
            marginBottom: attrFilteredAllLater.length ? '16px' : 0,
          }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--on-surface)', marginBottom: '6px' }}>
              {hasNowSpotsBeforeAttrFilter && activeAttrFilterCount > 0 ? t('no_spots_match_filters') : t('no_free_spots')}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--on-surface-3)' }}>
              {hasNowSpotsBeforeAttrFilter && activeAttrFilterCount > 0 ? '' : t('check_back_soon')}
            </div>
            {hasNowSpotsBeforeAttrFilter && activeAttrFilterCount > 0 && (
              <button
                onClick={() => handleAttrFiltersChange(EMPTY_ATTR_FILTERS)}
                style={{
                  marginTop: '14px', padding: '10px 20px', borderRadius: '10px',
                  border: '1.5px solid var(--primary)', background: '#fff',
                  color: 'var(--primary)', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t('clear_filters')}
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: attrFilteredAllLater.length ? '8px' : 0 }}>
            {filtered.map((spot) => (
              <SpotCard
                key={spot.id}
                spot={spot}
                timezone={timezone}
                disabled={false}
                isOwn={false}
                onClaim={() => tryClaimSpot(spot)}
                onManage={() => navigate(`/spots/${spot.id}`)}
                activeAttrFilters={attrFilters}
                t={t}
              />
            ))}
            {filteredOwn.map((spot) => (
              <SpotCard
                key={spot.id}
                spot={spot}
                timezone={timezone}
                disabled={false}
                isOwn={true}
                onClaim={() => {}}
                onManage={() => navigate(`/spots/${spot.id}`)}
                activeAttrFilters={attrFilters}
                t={t}
              />
            ))}
          </div>
        )}

        {/* ── Free later divider ── */}
        {attrFilteredAllLater.length > 0 && (
          <>
            <div style={{ margin: '24px 0 12px', textAlign: 'center' }}>
              <span style={{
                fontSize:      '11px',
                color:         'var(--on-surface-3)',
                fontWeight:    700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}>
                {t('available_later')}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[...filteredLater, ...filteredOwnLater].map((spot) => {
                const isOwn = spot.owner?.id === user?.id;
                return (
                  <div
                    key={spot.id}
                    onClick={isOwn ? () => navigate(`/spots/${spot.id}`) : undefined}
                    style={{
                      background:   '#ffffff',
                      borderRadius: '20px',
                      padding:      '20px',
                      boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
                      border:       isOwn ? '1.5px solid #bbf7d0' : '1px solid transparent',
                      transition:   'box-shadow 0.2s, border-color 0.2s',
                      cursor:       isOwn ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <div style={{
                        width:          '48px',
                        height:         '48px',
                        borderRadius:   '12px',
                        background:     isOwn ? '#dcfce7' : 'var(--primary-container)',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        flexShrink:     0,
                      }}>
                        <span style={{
                          fontFamily: 'var(--manrope)',
                          fontWeight: 800,
                          fontSize:   spot.spot_number.length > 4 ? '11px' : '14px',
                          color:      isOwn ? '#16a34a' : 'var(--primary)',
                          lineHeight: 1,
                        }}>
                          {spot.spot_number}
                        </span>
                      </div>
                      <div style={{
                        display:        'flex',
                        flexDirection:  'column',
                        alignItems:     'flex-end',
                        gap:            '4px',
                        flexShrink:     0,
                      }}>
                        <span style={{
                          background:    '#ede7f6',
                          color:         '#4527a0',
                          fontSize:      '10px',
                          fontWeight:    700,
                          padding:       '4px 9px',
                          borderRadius:  '6px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          whiteSpace:    'nowrap',
                        }}>
                          {t('from')} {isTodayInTz(spot.availability.starts_at, timezone)
                            ? formatLocalTime(spot.availability.starts_at, timezone, 'time', language)
                            : formatLocalTime(spot.availability.starts_at, timezone, 'datetime-weekday', language)}
                        </span>
                        {spot.availability.until && (
                          <span style={{
                            background:    '#ede7f6',
                            color:         '#4527a0',
                            fontSize:      '10px',
                            fontWeight:    700,
                            padding:       '4px 9px',
                            borderRadius:  '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            whiteSpace:    'nowrap',
                          }}>
                            {t('until')} {isTodayInTz(spot.availability.until, timezone)
                              ? formatLocalTime(spot.availability.until, timezone, 'time', language)
                              : formatLocalTime(spot.availability.until, timezone, 'datetime-weekday', language)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{
                      fontFamily:   'var(--manrope)',
                      fontWeight:   700,
                      fontSize:     '17px',
                      color:        'var(--on-surface)',
                      marginBottom: '4px',
                      lineHeight:   1.2,
                    }}>
                      {t(spot.level_code) || spot.level_label} · {spot.spot_number}
                    </div>

                    <div style={{
                      fontSize:     '13px',
                      color:        'var(--on-surface-2)',
                      marginBottom: '8px',
                    }}>
                      {spot.description ?? 'No description'}
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <AttributeChips attributes={spot.attributes} activeFilters={attrFilters} />
                    </div>

                    <div style={{
                      display:        'flex',
                      justifyContent: 'space-between',
                      alignItems:     'center',
                      gap:            '12px',
                    }}>
                      {isOwn ? (
                        <span style={{
                          display:      'inline-flex',
                          alignItems:   'center',
                          gap:          '4px',
                          background:   '#dcfce7',
                          color:        '#15803d',
                          fontSize:     '12px',
                          fontWeight:   700,
                          padding:      '4px 10px',
                          borderRadius: '9999px',
                        }}>
                          <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>home</span>
                          {t('your_spot')}
                        </span>
                      ) : (
                        <div style={{
                          display:     'flex',
                          alignItems:  'center',
                          gap:         '6px',
                          fontSize:    '12px',
                          color:       'var(--on-surface-2)',
                          fontWeight:  500,
                        }}>
                          <span style={{
                            display:        'inline-flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            width:          '16px',
                            height:         '16px',
                            borderRadius:   '50%',
                            backgroundColor: 'var(--surface-low)',
                            color:          'var(--primary)',
                            fontSize:       '11px',
                            fontWeight:     700,
                          }}>
                            i
                          </span>
                          <span>{t('apt_short')} {spot.owner?.apartment}</span>
                        </div>
                      )}

                      {isOwn ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/spots/${spot.id}`); }}
                          style={{
                            height:       '36px',
                            padding:      '0 20px',
                            borderRadius: '8px',
                            border:       '1.5px solid #16a34a',
                            background:   'transparent',
                            color:        '#16a34a',
                            fontSize:     '13px',
                            fontWeight:   600,
                            cursor:       'pointer',
                            whiteSpace:   'nowrap',
                            transition:   'opacity 0.15s',
                            flexShrink:   0,
                          }}
                        >
                          {t('manage')}
                        </button>
                      ) : (
                        <button
                          onClick={() => tryClaimSpot(spot)}
                          style={{
                            height:       '36px',
                            padding:      '0 20px',
                            borderRadius: '8px',
                            border:       'none',
                            background:   'linear-gradient(135deg, #005bc0, #004fa9)',
                            color:        '#f7f7ff',
                            fontSize:     '13px',
                            fontWeight:   600,
                            cursor:       'pointer',
                            whiteSpace:   'nowrap',
                            transition:   'opacity 0.15s',
                            flexShrink:   0,
                          }}
                        >
                          {t('claim')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Claim modal */}
      {claimTarget && (
        <ClaimModal
          spot={claimTarget}
          onClose={() => setClaimTarget(null)}
          onClaimed={handleClaimed}
          timezone={timezone}
          t={t}
          language={language}
          direction={direction}
        />
      )}

      {/* Filter sheet */}
      {showFilterSheet && (
        <FilterSheet
          filters={attrFilters}
          onChange={handleAttrFiltersChange}
          onClose={() => setShowFilterSheet(false)}
          t={t}
        />
      )}

      {/* Offer-my-spot picker (only reached when the user owns 2+ spots) */}
      {showOfferSheet && mySpots && (
        <OfferSpotSheet
          spots={mySpots}
          onPick={(spotId) => navigate(`/spots/${spotId}/free`)}
          onClose={() => setShowOfferSheet(false)}
          t={t}
        />
      )}

      <BottomNav />

      {/* Sign out confirm */}
      {showSignOutConfirm && (
        <>
          <div
            onClick={() => setShowSignOutConfirm(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 300 }}
          />
          <div style={{
            position:        'fixed',
            top:             '50%',
            left:            '50%',
            transform:       'translate(-50%, -50%)',
            width:           'calc(100% - 48px)',
            maxWidth:        '320px',
            backgroundColor: '#ffffff',
            borderRadius:    '20px',
            padding:         '24px 20px 16px',
            zIndex:          301,
            boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.12)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
              {t('sign_out_title')}
            </div>
            <div className="body-md" style={{ marginBottom: '20px' }}>
              {t('sign_in_again')}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowSignOutConfirm(false)} className="btn-secondary" style={{ flex: 1, height: '44px' }}>
                {t('cancel')}
              </button>
              <button onClick={() => { setShowSignOutConfirm(false); void signOut(); }} className="btn-primary" style={{ flex: 1, height: '44px' }}>
                {t('sign_out')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  background: 'none',
  border:     'none',
  cursor:     'pointer',
  color:      'var(--on-surface-3)',
  padding:    '8px',
  display:    'flex',
  alignItems: 'center',
  fontSize:   '20px',
  lineHeight: 1,
};

const modalBtn: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  height:       '44px',
  borderRadius: '8px',
  border:       'none',
  background:   'linear-gradient(135deg, #005bc0, #004fa9)',
  color:        '#f7f7ff',
  fontSize:     '14px',
  fontWeight:   600,
  cursor:       'pointer',
};
